import type { DeploymentSummary } from "@situate/contracts/platform";

/**
 * Deployment permission code.
 *
 * These codes are resolved by platform-api from active role assignments.
 * Permission codes follow the convention: deployment.<feature>.<action>
 *
 * See: @situate/contracts/authorization/permissions.yaml
 */
export type DeploymentPermissionCode = string;

/**
 * Check if a deployment grants a specific permission.
 *
 * @param deployment - Deployment with access context
 * @param permissionCode - Permission code to check (e.g., "deployment.dashboard.read")
 * @returns true if the deployment's permission_codes includes the given code
 *
 * @example
 * ```ts
 * if (hasDeploymentPermission(deployment, "deployment.dashboard.read")) {
 *   // Show dashboard link
 * }
 * ```
 */
export function hasDeploymentPermission(
  deployment: DeploymentSummary,
  permissionCode: DeploymentPermissionCode
): boolean {
  // Fail closed if permission_codes is missing or invalid
  if (!deployment.access?.permission_codes) {
    return false;
  }

  return deployment.access.permission_codes.includes(permissionCode);
}

/**
 * Check if a deployment grants any of the specified permissions.
 *
 * @param deployment - Deployment with access context
 * @param permissionCodes - Array of permission codes to check
 * @returns true if the deployment has at least one of the given permissions
 *
 * @example
 * ```ts
 * // Show analytics if user has any analytics permission
 * if (hasAnyDeploymentPermission(deployment, [
 *   "deployment.analytics.read",
 *   "deployment.analytics.population.read",
 * ])) {
 *   // Show analytics link
 * }
 * ```
 */
export function hasAnyDeploymentPermission(
  deployment: DeploymentSummary,
  permissionCodes: DeploymentPermissionCode[]
): boolean {
  // Fail closed if permission_codes is missing or invalid
  if (!deployment.access?.permission_codes) {
    return false;
  }

  return permissionCodes.some((code) => deployment.access.permission_codes.includes(code));
}

/**
 * Check if a deployment grants all of the specified permissions.
 *
 * @param deployment - Deployment with access context
 * @param permissionCodes - Array of permission codes to check
 * @returns true if the deployment has all of the given permissions
 */
export function hasAllDeploymentPermissions(
  deployment: DeploymentSummary,
  permissionCodes: DeploymentPermissionCode[]
): boolean {
  // Fail closed if permission_codes is missing or invalid
  if (!deployment.access?.permission_codes) {
    return false;
  }

  return permissionCodes.every((code) => deployment.access.permission_codes.includes(code));
}

// ----- Named permission helpers for common page/feature checks -----

/**
 * Check if user can view the dashboard page.
 */
export function canViewDashboard(deployment: DeploymentSummary): boolean {
  return hasDeploymentPermission(deployment, "deployment.dashboard.read");
}

/**
 * Check if user can view the live view page.
 */
export function canViewLiveView(deployment: DeploymentSummary): boolean {
  return hasDeploymentPermission(deployment, "deployment.live.read");
}

/**
 * Check if user can view live heatmap data.
 */
export function canViewLiveHeatmap(deployment: DeploymentSummary): boolean {
  return hasDeploymentPermission(deployment, "deployment.live.heatmap.read");
}

/**
 * Check if user can view live tracks data.
 */
export function canViewLiveTracks(deployment: DeploymentSummary): boolean {
  return hasDeploymentPermission(deployment, "deployment.live.tracks.read");
}

/**
 * Check if user can list the deployment's areas.
 */
export function canViewAreas(deployment: DeploymentSummary): boolean {
  return hasDeploymentPermission(deployment, "deployment.areas.read");
}

/**
 * Check if user can view an area's areas of interest (AOIs).
 */
export function canViewAois(deployment: DeploymentSummary): boolean {
  return hasDeploymentPermission(deployment, "deployment.aois.read");
}

/**
 * Check if user can view live sensors data.
 */
export function canViewLiveSensors(deployment: DeploymentSummary): boolean {
  return hasDeploymentPermission(deployment, "deployment.live.sensors.read");
}

/**
 * Check if user can view the analytics page.
 */
export function canViewAnalytics(deployment: DeploymentSummary): boolean {
  return hasDeploymentPermission(deployment, "deployment.analytics.read");
}

/**
 * Check if user can view population analytics.
 */
export function canViewPopulationAnalytics(deployment: DeploymentSummary): boolean {
  return hasDeploymentPermission(deployment, "deployment.analytics.population.read");
}

/** Check if dashboard population data may be requested. */
export const canViewDashboardPopulation = canViewPopulationAnalytics;

/**
 * Check if user can view density analytics.
 */
export function canViewDensityAnalytics(deployment: DeploymentSummary): boolean {
  return hasDeploymentPermission(deployment, "deployment.analytics.density.read");
}

/**
 * Check if user can view throughput analytics.
 */
export function canViewThroughputAnalytics(deployment: DeploymentSummary): boolean {
  return hasDeploymentPermission(deployment, "deployment.analytics.throughput.read");
}

/** Check if dashboard throughput data may be requested. */
export const canViewDashboardThroughput = canViewThroughputAnalytics;

/**
 * Check if user can view the notifications page.
 */
export function canViewNotifications(deployment: DeploymentSummary): boolean {
  return hasDeploymentPermission(deployment, "deployment.notifications.read");
}

/** Check if user can download generated reports. */
export function canDownloadGeneratedReports(deployment: DeploymentSummary): boolean {
  return hasDeploymentPermission(deployment, "deployment.reports.read");
}

/** Check if dashboard notification data may be requested. */
export const canViewDashboardNotifications = canViewNotifications;

/** Check if dashboard capacity configuration may be requested. */
export function canViewDashboardCapacity(deployment: DeploymentSummary): boolean {
  return hasDeploymentPermission(deployment, "deployment.alert_thresholds.read");
}

/** Check if dashboard area names may be requested. */
export const canViewDashboardAreas = canViewAreas;

export type DashboardSourceName =
  "areas" | "population" | "capacity" | "throughput" | "notifications";

/** Canonical dashboard-source to deployment-permission mapping. */
export function canViewDashboardSource(
  deployment: DeploymentSummary,
  source: DashboardSourceName
): boolean {
  switch (source) {
    case "areas":
      return canViewDashboardAreas(deployment);
    case "population":
      return canViewDashboardPopulation(deployment);
    case "capacity":
      return canViewDashboardCapacity(deployment);
    case "throughput":
      return canViewDashboardThroughput(deployment);
    case "notifications":
      return canViewDashboardNotifications(deployment);
  }
}

/**
 * Check if user can resolve notifications.
 */
export function canResolveNotifications(deployment: DeploymentSummary): boolean {
  return hasDeploymentPermission(deployment, "deployment.notifications.resolve");
}

/**
 * Check if user can view the playback page.
 */
export function canViewPlayback(deployment: DeploymentSummary): boolean {
  return hasDeploymentPermission(deployment, "deployment.playback.read");
}

/**
 * Check if user can view the settings page.
 */
export function canViewSettings(deployment: DeploymentSummary): boolean {
  return hasDeploymentPermission(deployment, "deployment.settings.read");
}

/**
 * Check if user can modify deployment settings.
 */
export function canModifySettings(deployment: DeploymentSummary): boolean {
  return hasDeploymentPermission(deployment, "deployment.settings.write");
}

/**
 * Check if user can modify areas of interest (AOIs).
 */
export function canModifyAOIs(deployment: DeploymentSummary): boolean {
  return hasDeploymentPermission(deployment, "deployment.aois.write");
}

/**
 * Check if the user has an administrative role for this deployment scope.
 */
export function hasAdministrativeRole(deployment: DeploymentSummary): boolean {
  return deployment.access.role_keys.some((roleKey) => roleKey.endsWith("_admin"));
}

/**
 * Check if an administrator can modify alert thresholds.
 */
export function canModifyAlertThresholds(deployment: DeploymentSummary): boolean {
  return (
    hasAdministrativeRole(deployment) &&
    hasDeploymentPermission(deployment, "deployment.alert_thresholds.write")
  );
}

/**
 * Check if user can export dashboard data.
 */
export function canExportDashboard(deployment: DeploymentSummary): boolean {
  return hasDeploymentPermission(deployment, "deployment.dashboard.export");
}

/**
 * Check if user can export live view data.
 */
export function canExportLiveView(deployment: DeploymentSummary): boolean {
  return hasDeploymentPermission(deployment, "deployment.live.export");
}

/**
 * Check if user can export notifications data.
 */
export function canExportNotifications(deployment: DeploymentSummary): boolean {
  return hasDeploymentPermission(deployment, "deployment.notifications.export");
}

/**
 * Check if user can export playback data.
 */
export function canExportPlayback(deployment: DeploymentSummary): boolean {
  return hasDeploymentPermission(deployment, "deployment.playback.export");
}
